import Badge from '@/common/components/Badge'
import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import { CurrencyTextField } from '@/common/components/CurrencyTextField'
import SelectController from '@/common/components/SelectController'
import { UnitTextField } from '@/common/components/UnitTextField'
import { options as unitForQuantity } from '@/common/constants/unitForQuantityOptions'
import { formatCurrency } from '@/common/utils/formatters'
import { getPriceRuleOptions } from '@/features/publicCompanyLeadsFunnel/constants/priceRuleOptions.constant'
import { PublicCompanyLeadData } from '@/features/publicCompanyLeadsFunnel/types/publicCompanyLead'
import { FileBoxIcon, MapPinnedIcon, Trash2Icon } from 'lucide-react'
import { Controller, UseFieldArrayRemove, useFormContext } from 'react-hook-form'
import {
  PublicCompanyLeadsFunnelContractProductCategoryValidationSchema,
  PublicCompanyLeadsFunnelUpdateContractProductValidationSchema,
} from '../schemas/update.schema'
import { ContractProductFixedPrices } from './ContractProductFixedPrices'
import { unitiesDecimals } from '@/common/constants/unities.constant'

interface ContractProductCategoryProductItemProps {
  lead?: PublicCompanyLeadData
  contractProductIndex: number
  contractProduct: PublicCompanyLeadsFunnelUpdateContractProductValidationSchema
  removeContractProduct: UseFieldArrayRemove
}

export const ContractProductCategoryProductItem = ({
  lead,
  contractProductIndex,
  contractProduct,
  removeContractProduct,
}: ContractProductCategoryProductItemProps) => {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<PublicCompanyLeadsFunnelContractProductCategoryValidationSchema>()

  const unitForQuantitySymbol = unitForQuantity.find(
    (unit) => unit.value === contractProduct?.product?.unit_for_quantity,
  )?.symbol

  const watchProductPriceRule = watch(`contract_product_category_products.${contractProductIndex}.price_rule`)
  const watchFilterByRegion = watch(`contract_product_category_products.${contractProductIndex}.filterByRegion`)

  const handleChangeFilterByRegion = ({
    valueIndex,
    productPriceId,
  }: {
    valueIndex: number
    productPriceId?: string
  }) => {
    setValue(`contract_product_category_products.${contractProductIndex}.filterByRegion`, true)

    if (productPriceId) {
      setValue(
        `contract_product_category_products.${contractProductIndex}.contract_product_category_product_prices.${valueIndex}.value`,
        formatCurrency(0),
      )
    }
  }

  return (
    <Box className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <Badge
          Icon={FileBoxIcon}
          iconProps={{ className: 'size-6', strokeWidth: 1 }}
          text={contractProduct?.product?.display_name}
          className="rounded-sm flex bg-neutral-op-8 w-fit flex-row-reverse overflow-hidden !p-0 pe-2 gap-0"
          overwriteClassName
          contentClassName="px-3 py-2"
        />

        <Button className="px-2" variant="outline-error" onClick={() => removeContractProduct(contractProductIndex)}>
          <Trash2Icon strokeWidth={1} />
        </Button>
      </div>

      {lead?.contract?.require_product_credit_quota && (
        <>
          <div className="flex gap-2">
            <Controller
              control={control}
              name={`contract_product_category_products.${contractProductIndex}.global_value_in_currency`}
              render={({ field: { onBlur, ...rest } }) => (
                <CurrencyTextField
                  dataTestid={`contract-product-${contractProductIndex}-value_in_currency_input`}
                  label="Cota total em valor"
                  markAsRequired
                  className="w-1/2"
                  {...rest}
                  errorMessage={
                    errors?.contract_product_category_products?.[contractProductIndex]?.global_value_in_currency
                      ?.message
                  }
                  disabled={false}
                />
              )}
            />

            <Controller
              control={control}
              name={`contract_product_category_products.${contractProductIndex}.global_value_in_unit_for_consumption`}
              render={({ field: { onBlur, ...rest } }) => (
                <UnitTextField
                  dataTestid={`contract-product-${contractProductIndex}-value_in_unit_for_consumption_input`}
                  label="Cota total em quantidade"
                  unit={unitForQuantitySymbol}
                  markAsRequired
                  className="w-1/2"
                  decimals={contractProduct?.product?.unit_for_quantity ? unitiesDecimals[contractProduct?.product?.unit_for_quantity] : 2}
                  {...rest}
                  errorMessage={
                    errors?.contract_product_category_products?.[contractProductIndex]
                      ?.global_value_in_unit_for_consumption?.message
                  }
                  disabled={false}
                />
              )}
            />
          </div>

          <SelectController
            control={control}
            name={`contract_product_category_products.${contractProductIndex}.price_rule`}
            label="Regras de preço"
            markAsRequired
            defaultValue={contractProduct.price_rule}
            options={getPriceRuleOptions({ isFuel: contractProduct?.product?.category?.perma_name === "fuel" }) || []}
            isDisabled={false}
            errorMessage={errors?.contract_product_category_products?.[contractProductIndex]?.price_rule?.message}
          />

          {watchProductPriceRule?.value === 'fixed' && (
            <>
              {watchFilterByRegion ? (
                <ContractProductFixedPrices productIndex={contractProductIndex} disabled={false} />
              ) : (
                <>
                  <Controller
                    control={control}
                    name={`contract_product_category_products.${contractProductIndex}.contract_product_category_product_prices.0.value`}
                    render={({ field: { onBlur, ...rest } }) => (
                      <CurrencyTextField
                        dataTestid={`contract-product-${contractProductIndex}-price_rule_value-0`}
                        label="Preço fixo a ser praticado"
                        {...rest}
                        errorMessage={
                          errors?.contract_product_category_products?.[contractProductIndex]
                            ?.contract_product_category_product_prices?.[0]?.value?.message
                        }
                        disabled={false}
                        markAsRequired
                      />
                    )}
                  />

                  <Button
                    type="button"
                    variant="link"
                    className="gap-2 ml-auto hover:no-underline"
                    name={`contract_product_price_rule_by_region_${contractProductIndex}`}
                    onClick={() =>
                      handleChangeFilterByRegion({
                        valueIndex: 0,
                        productPriceId: contractProduct.contract_product_category_product_prices?.[0]?.id || '',
                      })
                    }
                    disabled={false}
                  >
                    <MapPinnedIcon strokeWidth={1} className="size-4" />
                    <p className="text-[11px]">Filtrar por região</p>
                  </Button>
                </>
              )}
            </>
          )}
        </>
      )}
    </Box>
  )
}
